import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import {Button, IconButton, TextField, Typography, Box, Theme} from '@mui/material'
import React, {useState} from 'react'
import {Controller, useForm} from 'react-hook-form'
import {useTranslation} from 'react-i18next'
import {makeStyles} from 'tss-react/mui'

import {DeleteAnnotationDialog} from './DeleteAnnotationDialog'
import {DiscardAnnotationChangesDialog} from './DiscardDeliveryAnnotationChangesDialog'

import type {DeliveryAnnotationAudit} from '@hconnect/apiclient'
import {
  DELIVERY_ANNOTATION_MAX_CHARS,
  buildDeliveryAnnotationResolver,
  type DeliveryAnnotationFormSchema
} from '@hconnect/tuc-processes/deliveryAnnotations'
import {dateFormatter, timeFormatter} from '@hconnect/uikit'

interface DeliveryAnnotationFormViewProps {
  onSave: (text: string) => Promise<void>
  onCancel: () => void
  onClose: () => void
  onDelete?: () => Promise<void>
  onChangesDetected?: (hasChanges: boolean) => void
  isLoading?: boolean
  hasExistingAnnotation?: boolean
  initialValue?: string
  audit?: DeliveryAnnotationAudit
}

const useStyles = makeStyles()((theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2)
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: theme.spacing(2),
    borderBottom: `1px solid #E8ECF0`
  },
  title: {
    fontWeight: 600,
    fontSize: '20px',
    lineHeight: '24px',
    color: '#29333D'
  },
  helperText: {
    fontSize: '12px',
    lineHeight: '16px',
    color: '#52667A',
    marginBottom: theme.spacing(1)
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      minHeight: '136px',
      alignItems: 'flex-start',
      '& textarea': {
        minHeight: '120px'
      }
    }
  },
  charCounter: {
    fontSize: '12px',
    lineHeight: '16px',
    color: '#52667A',
    textAlign: 'right'
  },
  actionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid #E8ECF0`
  },
  saveButton: {
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '16px',
    padding: '12px 16px',
    '&:disabled': {
      opacity: 0.3
    }
  },
  cancelButton: {
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '16px',
    padding: '12px 16px',
    borderColor: '#E8ECF0',
    color: '#29333D'
  },
  headerActions: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center'
  },
  iconButton: {
    padding: '8px',
    boxShadow: 'none',
    border: 'none',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)'
    },
    '&:disabled': {
      opacity: 0.3
    }
  },
  changelogSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5)
  },
  changelogTitle: {
    fontSize: '16px',
    lineHeight: '24px',
    fontWeight: 600,
    color: '#52667a'
  },
  changelogList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  changelogItem: {
    fontSize: '12px',
    lineHeight: '16px',
    fontWeight: 400,
    color: '#52667a'
  }
}))

export const DeliveryAnnotationFormView: React.FC<DeliveryAnnotationFormViewProps> = ({
  onSave,
  onCancel,
  onClose,
  onDelete,
  onChangesDetected,
  isLoading = false,
  hasExistingAnnotation = false,
  initialValue = '',
  audit
}) => {
  const {t, i18n: {language}} = useTranslation()
  const {classes} = useStyles()

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: {isValid, isDirty, isSubmitting}
  } = useForm<DeliveryAnnotationFormSchema>({
    mode: 'onChange',
    resolver: buildDeliveryAnnotationResolver(),
    defaultValues: {
      content: initialValue
    }
  })

  const [isDeleting, setIsDeleting] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showDiscardDialog, setShowDiscardDialog] = useState(false)

  const content = watch('content')
  const charCount = content.length
  const isBusy = isSubmitting || isDeleting
  const isSaveDisabled = !isValid || isBusy || isLoading

  // Notify parent about changes
  React.useEffect(() => {
    onChangesDetected?.(isDirty)
  }, [isDirty, onChangesDetected])

  const handleClose = () => {
    if (isDirty) {
      setShowDiscardDialog(true)
    } else {
      onClose()
    }
  }

  const handleDiscardChanges = () => {
    setShowDiscardDialog(false)
    onClose()
  }

  const onSubmit = async (data: DeliveryAnnotationFormSchema) => {
    try {
      await onSave(data.content)
    } catch (error) {
      console.error('Failed to save annotation:', error)
    }
  }

  const handleDelete = async () => {
    if (!onDelete) return
    setIsDeleting(true)
    try {
      await onDelete()
      setShowDeleteDialog(false)
    } catch (error) {
      console.error('Failed to delete annotation:', error)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Allow Ctrl+Enter or Cmd+Enter to save
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && isValid) {
      handleSubmit(onSubmit)()
    }
  }

  return (
    <div className={classes.container}>
      {/* Header */}
      <div className={classes.header}>
        <Typography className={classes.title}>{t('annotation')}</Typography>
        <div className={classes.headerActions}>
          {hasExistingAnnotation && onDelete ? (
            <IconButton
              onClick={() => setShowDeleteDialog(true)}
              size="small"
              disableRipple
              className={classes.iconButton}
              data-test-id="annotation-delete-button"
            >
              <DeleteOutlineIcon sx={{fontSize: 16}} />
            </IconButton>
          ) : (
            <IconButton
              onClick={() => setValue('content', '', {shouldValidate: true, shouldDirty: true})}
              disabled={charCount === 0}
              size="small"
              disableRipple
              className={classes.iconButton}
              data-test-id="annotation-clear-button"
            >
              <DeleteOutlineIcon sx={{fontSize: 16}} />
            </IconButton>
          )}
          <IconButton
            onClick={handleClose}
            size="small"
            disableRipple
            className={classes.iconButton}
            data-test-id="annotation-form-close"
          >
            <CloseIcon sx={{fontSize: 16}} />
          </IconButton>
        </div>
      </div>

      {/* Content */}
      <Box>
        {/* Helper text */}
        <Typography className={classes.helperText}>
          {t('annotationHelperText')}
        </Typography>

        {/* Textarea */}
        <Controller
          name="content"
          control={control}
          render={({field, fieldState}) => (
            <TextField
              {...field}
              multiline
              fullWidth
              onKeyDown={handleKeyDown}
              variant="outlined"
              className={classes.textField}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              inputProps={{
                maxLength: DELIVERY_ANNOTATION_MAX_CHARS,
                'data-test-id': 'delivery-annotation-textarea'
              }}
              autoFocus
            />
          )}
        />

        {/* Character counter */}
        <Typography className={classes.charCounter}>
          {charCount} / {DELIVERY_ANNOTATION_MAX_CHARS}
        </Typography>

        {/* Changelog - only show for existing annotations */}
        {audit && hasExistingAnnotation && (
          <div className={classes.changelogSection}>
            <div className={classes.changelogTitle}>{t('changelog')}</div>
            <div className={classes.changelogList}>
              <div className={classes.changelogItem}>
                {t('changelogCreated', {
                  date: dateFormatter(audit.createdAt, language),
                  time: timeFormatter(audit.createdAt, language),
                  user: audit.createdByUserName
                })}
              </div>
              {audit.updatedAt && audit.updatedByUserName && (
                <div className={classes.changelogItem}>
                  {t('changelogEdited', {
                    date: dateFormatter(audit.updatedAt, language),
                    time: timeFormatter(audit.updatedAt, language),
                    user: audit.updatedByUserName
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </Box>

      {/* Actions */}
      <div className={classes.actionsContainer}>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          disabled={isSaveDisabled}
          onClick={handleSubmit(onSubmit)}
          className={classes.saveButton}
          data-test-id="delivery-annotation-save-button"
        >
          {t('save')}
        </Button>
        <Button
          fullWidth
          variant="outlined"
          onClick={onCancel}
          className={classes.cancelButton}
          data-test-id="delivery-annotation-cancel-button"
        >
          {t('backToDeliveryDetails')}
        </Button>
      </div>

      {/* Delete Confirmation Dialog */}
      <DeleteAnnotationDialog
        open={showDeleteDialog}
        onCancel={() => setShowDeleteDialog(false)}
        onDelete={handleDelete}
        isDeleting={isDeleting}
      />

      {/* Discard Changes Dialog */}
      <DiscardAnnotationChangesDialog
        open={showDiscardDialog}
        onCancel={() => setShowDiscardDialog(false)}
        onDiscard={handleDiscardChanges}
      />
    </div>
  )
}
